import { atom, atomFamily, DefaultValue, selector, selectorFamily } from 'recoil';
import { jsonArray, jsonValue } from '../types/jsonValue';
import {
  Scenario,
  scenarioState, ScenarioItem, scenarioItemState,
} from './scenarioState';
import { currentScenarioItemState } from './displayedScenarioItemState';
import { scenarioDefaultFormQuery } from './query';

export type FormValues = {
  [id: string]: jsonValue | jsonArray
}

export const formState = atom<FormValues>({
  key: 'form',
  default: {},
});

const initInputFormState = atomFamily<{initialized: boolean}, string | undefined>({
  key: 'initInputForm',
  default: () => ({initialized: false}),
  dangerouslyAllowMutability: true,
});


export const inputFormState = atomFamily<FormValues, string | undefined>({
  key: 'inputForm',
  default: () => ({}),
  effects: scenarioItemId => [
    ({getPromise, setSelf}) => {
      (async () =>  {
        const state = await getPromise(initInputFormState(scenarioItemId));
        if (state.initialized) {
          return;
        }
        state.initialized = true;

        const form = await getPromise(formState);
        const scenarioItem = findItemByItemId(await getPromise(scenarioState), scenarioItemId);
        if (scenarioItem == null) {
          return
        }

        const defaultForm = await getPromise(scenarioDefaultFormQuery);
        const inputForm = scenarioItem.components.reduce<FormValues>((ret, c) => {
          ret[c.id] = defaultForm[c.id];
          if (c.id in form) {
            ret[c.id] = form[c.id];
          }
          return ret;
        }, {})
        setSelf(inputForm);
      })();
    }
  ]
});

export const currentFormValuesState = selector<{ [key: string]: jsonValue | jsonArray }>({
  key: 'currentFormValues',
  get: ({get}) => {
    const current = get(currentScenarioItemState);
    const inputting = get(inputFormState(current?.id));
    const form = get(formState);
    const ids = [...Object.keys(inputting), ...Object.keys(form)]
      .reduce<string[]>((ret, key) => ret.includes(key) ? ret : [...ret, key], []);

    return ids.reduce<{ [key: string]: jsonValue | jsonArray }>((ret, id) => {
      if (id in inputting) {
        ret[id] = inputting[id];
      } else {
        ret[id] = get(formState)[id];
      }
      return ret;
    }, {});
  },
});

export const inputFormValueState = selectorFamily<jsonValue | jsonArray, string>({
  key: 'inputFormValue',
  get: (componentId) => ({get}) => {
    const scenarioItem = findItemByComponentId(get(scenarioState), componentId);
    const data = get(inputFormState(scenarioItem?.id || ''));
    return data[componentId];
  },
  set: (componentId) => ({set, get}, value) => {
    const scenarioItem = findItemByComponentId(get(scenarioState), componentId);
    set(inputFormState(scenarioItem?.id), old => {
      return {...old, ...{[componentId]: value}} as { [key: string]: jsonValue }
    });
    set(formState, prev => ({...prev, [componentId]: value as jsonValue | jsonArray}))
  },
});
export const inputFormChangedState = selectorFamily<boolean, string>({
  key: 'inputFormChangedState',
  get(componentId) {
    return ({get}) => {
      const inputValue = get(inputFormValueState(componentId));
      const formValue = get(formState)[componentId];
      return inputValue !== formValue;
    }
  }
});

export const inputErrorsState = atom<{[itemId: string]: string}>({
  key: 'inputErrors',
  default: {}
});
export const inputErrorState = selectorFamily<string | null, string>({
  key: 'inputError',
  get: (componentId: string) => ({get}) => {
    const value = get(inputErrorsState)[componentId];
    return value != null ? value : null;
  },
  set: (componentId: string) => ({set}, value ) => {
    set(inputErrorsState, prev => {
      if (value instanceof DefaultValue) {
        return prev;
      }
      if (value == null) {
        const next = {...prev};
        delete next[componentId];
        return next;
      } else {
        return {...prev, [componentId]: value};
      }
    })
  }
});
export const inputItemErrorsState = selectorFamily<{[componentId: string]: string}, string>({
  key: 'inputItemErrors',
  get: (itemId: string) => ({get}) => {
    const errors = get(inputErrorsState);
    const item = get(scenarioItemState(itemId));
    return item.components.reduce<{[componentId: string]: string}>((ret, c) => {
      if (errors[c.id] != null) {
        ret[c.id] = errors[c.id];
      }
      return ret;
    }, {});
  }
});

const findItemByItemId = (scenario: Scenario, itemId: string | undefined): ScenarioItem | undefined => {
  return scenario.items.find(i => i.id === itemId);
}

const findItemByComponentId = (scenario: Scenario, componentId: string | undefined): ScenarioItem | undefined => {
  if (componentId == null) {
    return;
  }
  return scenario.items.find(i => i.components.map(c => c.id).includes(componentId))
}
